<template>
  <div class="admin-layout-container">
    <n-layout has-sider position="absolute">
      <!-- SIDEBAR UTAMA -->
      <n-layout-sider
        bordered
        collapse-mode="transform"
        :collapsed-width="0"
        :width="200"
        show-trigger="arrow-circle"
        :inverted="false"
        v-model:collapsed="pageStore.isSidebarCollapsed"
        :collapsed-trigger-style="{
          top: '82px',
        }"
        :trigger-style="{
          top: '82px',
        }"
      >
        <div class="sidebar-wrapper">
          <!-- Header Logo / Brand Dynamic -->
          <div class="sidebar-brand-header" :class="{ 'is-collapsed': pageStore.isSidebarCollapsed }">
            <!-- Mode Collapsed -->
            <div v-if="pageStore.isSidebarCollapsed" class="brand-logo-collapsed">
              <img v-if="pageStore.appLogo" :src="pageStore.appLogo" alt="App Logo" class="brand-logo-img" />
            </div>

            <!-- Mode Expanded -->
            <template v-else>
              <div class="brand-logo-expanded">
                <img v-if="pageStore.appLogo" :src="pageStore.appLogo" alt="App Logo" class="brand-logo-img" />
                <span v-if="pageStore.appName" class="brand-title">{{ pageStore.appName }}</span>
              </div>
            </template>
          </div>

          <!-- Section Dynamic Navigation Menu -->
          <div class="sidebar-menu-container">
            <n-config-provider :theme-overrides="compactMenuTheme">
              <n-menu
                :inverted="false"
                :collapsed="pageStore.isSidebarCollapsed"
                :collapsed-width="0"
                :collapsed-icon-size="18"
                :icon-size="18"
                :root-indent="16"
                :indent="16"
                :options="(pageStore.menuOptions as any[]) ?? []"
                :value="activeMenuKey"
              />
            </n-config-provider>
          </div>

          <!-- Section User Info & Logout -->
          <div v-if="pageStore.userInfo" class="sidebar-footer" :class="{ 'is-collapsed': pageStore.isSidebarCollapsed }">
            <!-- Mode Collapsed: avatar saja, klik -> dropdown -->
            <div v-if="pageStore.isSidebarCollapsed" class="footer-collapsed">
              <n-dropdown trigger="click" :options="userDropdownOptions" @select="handleUserDropdownSelect">
                <div class="user-avatar-btn">
                  <img v-if="pageStore.userInfo.avatarUrl" :src="pageStore.userInfo.avatarUrl" alt="User Avatar" class="user-avatar-img" />
                  <span v-else class="user-avatar-fallback">{{ userInitial }}</span>
                </div>
              </n-dropdown>
            </div>

            <!-- Mode Expanded: nama + email + tombol logout -->
            <template v-else>
              <div class="footer-expanded">
                <n-dropdown trigger="click" :options="userDropdownOptions" @select="handleUserDropdownSelect">
                  <div class="user-info-btn">
                    <div class="user-avatar-btn">
                      <img v-if="pageStore.userInfo.avatarUrl" :src="pageStore.userInfo.avatarUrl" alt="User Avatar" class="user-avatar-img" />
                      <span v-else class="user-avatar-fallback">{{ userInitial }}</span>
                    </div>
                    <div class="user-info-text">
                      <span class="user-name">{{ pageStore.userInfo.name }}</span>
                      <span v-if="pageStore.userInfo.email" class="user-email">{{ pageStore.userInfo.email }}</span>
                    </div>
                  </div>
                </n-dropdown>

                <button class="logout-btn" type="button" @click="handleLogout">
                  <Icon icon="mdi:logout" width="16" height="16" />
                </button>
              </div>
            </template>
          </div>
        </div>
      </n-layout-sider>

      <div id="layout-slot-sidebar"></div>
      <Teleport defer to="#layout-slot-sidebar" />

      <!-- MAIN CONTENT AREA -->
      <n-layout class="main-content-area">
        <!-- Top Bar Header -->
        <header class="content-header">
          <div
            :class="{
              'header-left transition': true,
            }"
          >
            <slot name="header-breadcrumb">
              <n-breadcrumb>
                <n-breadcrumb-item v-for="(item, idx) in pageStore.breadcrumbs" :key="idx">
                  <!-- Jika ada path, gunakan RouterLink -->
                  <RouterLink v-if="item.path" :to="item.path">
                    {{ item.title }}
                  </RouterLink>
                  <!-- Jika tidak ada path, tampilkan teks biasa -->
                  <span v-else>{{ item.title }}</span>
                </n-breadcrumb-item>
              </n-breadcrumb>
            </slot>
          </div>

          <div class="header-right">
            <slot name="header-actions" />
          </div>
        </header>

        <!-- Main Body Content Slot -->
        <main class="page-body">
          <slot />
        </main>
      </n-layout>
    </n-layout>
  </div>
  <n-watermark
    v-if="!!pageStore.userInfo"
    :content="`${pageStore.userInfo?.name}\n${pageStore.userInfo?.email}`"
    font-color="rgba(128, 128, 128, .1)"
    cross
    fullscreen
    selectable
    :font-size="14"
    :line-height="16"
    :width="700"
    :height="200"
    :x-offset="0"
    :y-offset="50"
    :rotate="-15"
    :z-index="10000"
  >
  </n-watermark>
</template>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import type { GlobalThemeOverrides } from "naive-ui";
import { NBreadcrumb, NBreadcrumbItem, NConfigProvider, NDropdown, NLayout, NLayoutSider, NMenu } from "naive-ui";
import { computed } from "vue";
import { RouterLink } from "vue-router";
import { usePageStore } from "../stores/pageStore";

const pageStore = usePageStore();

const activeMenuKey = computed(() => pageStore.activeMenu);

const compactMenuTheme: GlobalThemeOverrides = {
  Menu: {
    itemHeight: "34px",
    fontSize: "13px",
  },
};

const userInitial = computed(() => (pageStore.userInfo?.name?.charAt(0) ?? "?").toUpperCase());

const userDropdownOptions = [
  {
    label: "Logout",
    key: "logout",
  },
];

function handleUserDropdownSelect(key: string) {
  if (key === "logout") {
    handleLogout();
  }
}

async function handleLogout() {
  await pageStore.triggerLogout();
}
</script>

<style scoped>
/* Memastikan tombol trigger bawaan Naive UI tampil di atas elemen lain */
:deep(.n-layout-sider-toggle) {
  z-index: 1000 !important;
}

.admin-layout-container {
  height: 100vh;
  position: relative;
}

.sidebar-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sidebar-brand-header {
  height: 48px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  white-space: nowrap;
  overflow: hidden;
  transition: all 0.3s ease;
}

.sidebar-brand-header.is-collapsed {
  padding: 0;
  justify-content: center;
}

.brand-logo-expanded {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-title {
  font-weight: 600;
  font-size: 15px;
  letter-spacing: -0.3px;
}

.brand-logo-collapsed {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 18px;
}

.brand-logo-img {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.sidebar-menu-container {
  flex: 1;
  overflow-y: auto;
  position: relative;
}

.sidebar-footer {
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0 16px;
  white-space: nowrap;
  overflow: hidden;
  transition: all 0.3s ease;
}

.sidebar-footer.is-collapsed {
  padding: 0;
  justify-content: center;
}

.footer-collapsed {
  display: flex;
  align-items: center;
  justify-content: center;
}

.footer-expanded {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.user-info-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  min-width: 0;
  flex: 1;
}

.user-avatar-btn {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e5e7eb;
  cursor: pointer;
}

.user-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-avatar-fallback {
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
}

.user-info-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}

.user-name {
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-email {
  font-size: 11px;
  color: #9ca3af;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.logout-btn {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: #6b7280;
  transition: background-color 0.15s ease;
}

.logout-btn:hover {
  background-color: #f3f4f6;
  color: #111827;
}

.main-content-area {
  background-color: #ffffff;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.content-header {
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 800;
  background-color: #ffffff;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.prefix-hash {
  color: #888;
  font-weight: 600;
  font-size: 14px;
}

.page-body {
  /* padding: 16px; */
  flex: 1;
  overflow: hidden;
  min-height: calc(100dvh - 48px);
  display: flex;
  flex-direction: column;
}
</style>
